import { HelpArticleContent } from '../../components/types';

export const shareVideo: HelpArticleContent = {
  title: 'Sharing Generated Videos',
  subtitle:
    'Once your personalised video is ready, it will be sent to the email address you provided during check-in. From there, you can download and share it with the world.',
  sections: [
    {
      id: 'one-click-share',
      heading: 'One-Click Share',
      intro:
        'Once your personalised video is ready, you can share it straight to your favourite social media or save it for later – in just one tap.',
      blocks: [
        {
          type: 'list',
          items: [
            {
              text: 'Tap on the coloured <b>Share</b> button below your video',
              image: {
                name: 'share-1-main',
                alt: 'Share Button',
                noPadding: true,
              },
            },
            {
              text: '<i>(Optional)</i> Depending on your event, you may be offered <b>pre-generated captions</b> provided by the organisers. Tap <b>Yes</b> or <b>No</b> to continue.',
              image: { name: 'share-2-caption-modal', alt: 'Caption Modal' },
            },
            {
              text: 'Your phone’s built-in sharing window will appear. <b>Choose the platform</b> you’d like to share your video on, or save it to your device by selecting <b>Save Video</b>.',
              image: { name: 'share-3-native-share', alt: 'Native Share' },
            },
          ],
        },
      ],
    },

    // temp hidden as Download button is hidden
    // {
    //   id: 'download',
    //   heading: 'Download Your Video',
    //   intro:
    //     'Download your personalised video directly to your device in a few simple steps.',
    //   note: 'For the best experience, we recommend using <a href="#one-click-share">One-Click Share</a> – it saves your video straight to your Photos app and makes sharing effortless. If you’d prefer, you can also download your video using the Download button below.',
    //   blocks: [
    //     {
    //       type: 'list',
    //       items: [
    //         {
    //           text: 'Tap on the <b>three dots</b> icon below your video',
    //           image: {
    //             name: 'share-1-main',
    //             alt: 'More Button',
    //             noPadding: true,
    //           },
    //         },
    //         {
    //           text: 'Select <b>Download</b> from the menu',
    //           image: {
    //             name: 'share-download-1',
    //             alt: 'Video More Menu',
    //             noPadding: true,
    //           },
    //         },
    //         {
    //           text: 'Tap <b>Download</b> again on the popped-up confirmation modal',
    //           image: {
    //             name: 'share-download-2',
    //             alt: 'Download Confirm Modal',
    //           },
    //         },
    //         {
    //           text: 'Your video will be saved to your <b>Files</b> app',
    //           note: 'To download straight to your Photos app, use the <a href="#one-click-share">One-Click Share</a> option instead.',
    //         },
    //       ],
    //     },
    //   ],
    // },

    {
      id: 'share-instagram',
      heading: 'Share to Instagram',
      intro:
        'Want to share your video on Instagram? Just follow these simple steps and you’ll have it posted in no time.',
      blocks: [
        {
          type: 'list',
          items: [
            {
              text: 'Tap on the coloured <b>Share</b> button below your video',
              image: {
                name: 'share-1-main',
                alt: 'Share Button',
                noPadding: true,
              },
            },
            {
              text: '<i>(Optional)</i> Depending on your event, you may be offered <b>pre-generated captions</b> provided by the organisers. Tap <b>Yes</b> to copy the caption to your clipboard.',
              image: { name: 'share-2-caption-modal', alt: 'Caption Modal' },
            },
            {
              text: 'Your phone’s built-in sharing window will appear. Choose <b>Instagram</b> from the list of available apps.',
              image: { name: 'share-3-native-share', alt: 'Native Share' },
            },
            {
              text: 'Select <b>Reel</b> as the sharing format',
              image: {
                name: 'share-insta-1',
                alt: 'Instagram Formats Selection',
                noPadding: true,
              },
            },
            {
              text: 'You’ll be redirected to the Instagram app – tap <b>Next</b> at the bottom of the screen',
              image: {
                name: 'share-insta-2',
                alt: 'Instagram Next',
                noPadding: true,
              },
            },
            {
              text: '<i>(If applicable)</i> Paste the caption you copied earlier',
              image: {
                name: 'share-insta-3',
                alt: 'Instagram Caption',
                noPadding: true,
              },
            },
            {
              text: 'Tag <b>@crowdclip</b> in your Reel so we can see and like it!',
              image: {
                name: 'share-insta-4',
                alt: 'Instagram Tag',
                noPadding: true,
              },
            },
            {
              text: 'Tap <b>Next</b> again and post your Reel',
              image: {
                name: 'share-insta-5',
                alt: 'Instagram Post',
                noPadding: true,
              },
            },
          ],
        },
      ],
    },

    {
      id: 'share-linkedin',
      heading: 'Share to LinkedIn',
      intro:
        'Want to share your video on LinkedIn? Just follow these steps to share it with your network.',
      blocks: [
        {
          type: 'list',
          items: [
            {
              text: 'Tap on the coloured <b>Share</b> button below your video',
              image: {
                name: 'share-1-main',
                alt: 'Share Button',
                noPadding: true,
              },
            },
            {
              text: '<i>(Optional)</i> Depending on your event, you may be offered <b>pre-generated captions</b> provided by the organisers. Tap <b>Yes</b> to copy the caption to your clipboard.',
              image: { name: 'share-2-caption-modal', alt: 'Caption Modal' },
            },
            {
              text: 'Your phone’s built-in sharing window will appear. Choose <b>Save Video</b> from the list below the apps.',
              image: { name: 'share-3-native-share', alt: 'Native Share' },
            },
            {
              text: 'Open the <b>LinkedIn</b> app',
              image: {
                name: 'share-linkedin-1',
                alt: 'LinkedIn App',
                noPadding: true,
              },
            },
            {
              text: 'Tap <b>Post</b> from the bottom menu',
              image: {
                name: 'share-linkedin-2',
                alt: 'LinkedIn Bottom Menu',
                noPadding: true,
              },
            },
            {
              text: '<i>(If applicable)</i> Paste the caption you copied earlier',
              image: {
                name: 'share-linkedin-3',
                alt: 'LinkedIn Caption',
                noPadding: true,
              },
            },
            {
              text: 'Select the <b>image</b> icon at the bottom left of the screen (just above the keyboard if it’s open)',
              image: {
                name: 'share-linkedin-4',
                alt: 'LinkedIn Attachments',
                noPadding: true,
              },
            },
            {
              text: 'Select the <b>video</b> you just downloaded',
              image: {
                name: 'share-linkedin-5',
                alt: 'LinkedIn Video Selection',
                noPadding: true,
              },
            },
            {
              text: 'Tap <b>Next</b> at the top of the screen',
              image: {
                name: 'share-linkedin-6',
                alt: 'LinkedIn Video Next',
                noPadding: true,
              },
            },
            {
              text: 'When both your caption and video are ready, tap <b>Post</b> at the top of the screen',
              image: {
                name: 'share-linkedin-7',
                alt: 'LinkedIn Post',
                noPadding: true,
              },
            },
          ],
        },
      ],
    },
  ],
};
